import { BToast } from "bootstrap-vue";
import i18n from "@/helpers/language";
import axios from "axios";
import moment from "moment";
import "moment/locale/sv";
import _ from "lodash";

export default {
  namespaced: true,
  state: {
    company: null,
    connected: true,
    pendingChanges: false,
    offlineQueueCount: 0,
    createdProtocolId: null,
    focusAreas: null,
    persons: null,
    departments: null,
    tags: null,
    esrstags: null,
    helpId: null,
    helpText: null,
    helpSearch: null,
    helpsFullList: null,
    favorites: JSON.parse(localStorage.getItem("favorites")) || [],
    recentRoutes: JSON.parse(localStorage.getItem("recentRoutes")) || [],
  },
  mutations: {
    setConnected: (state, value) => {
      state.connected = value;
    },
    setCompany: (state, company) => {
      state.company = company;
    },
    setPendingChanges: (state, value) => {
      state.pendingChanges = value;
    },
    setOfflineQueueCount: (state, value) => {
      state.offlineQueueCount = value;
    },
    setCreatedProtocolId: (state, value) => {
      state.createdProtocolId = value;
    },
    setFocusAreas: (state, focusAreas) => {
      state.focusAreas = focusAreas;
    },
    setPersons: (state, persons) => {
      state.persons = persons;
    },
    setDepartments: (state, departments) => {
      state.departments = departments;
    },
    setTags: (state, tags) => {
      state.tags = tags;
    },
    setEsrsTags: (state, tags) => {
      state.esrstags = tags;
    },
    clearData: (state) => {
      state.focusAreas = null;
      state.persons = null;
      state.departments = null;
      state.tags = null;
      state.esrstags = null;
      state.helpsFullList = null;
    },
    setHelpId: (state, id) => {
      state.helpId = id;
    },
    setHelpText: (state, text) => {
      state.helpText = text;
    },
    setHelpSearch: (state, text) => {
      state.helpSearch = text;
    },
    setHelpsFullList: (state, helps) => {
      state.helpsFullList = helps;
    },
    addFavorite(state, document) {
      state.favorites.push(document);
      localStorage.setItem("favorites", JSON.stringify(state.favorites));
    },
    removeFavorite(state, documentId) {
      state.favorites = state.favorites.filter((doc) => doc.id !== documentId);
      localStorage.setItem("favorites", JSON.stringify(state.favorites)); // Persist to localStorage
    },
    addRecentRoute(state, document) {
      state.recentRoutes = state.recentRoutes.filter(
        (doc) => !(doc.id === document.id && doc.userId === document.userId)
      );
      state.recentRoutes.unshift(document);
      if (state.recentRoutes.length > 10) {
        state.recentRoutes = state.recentRoutes.slice(0, 10);
      }
      localStorage.setItem("recentRoutes", JSON.stringify(state.recentRoutes));
    },
    removeRecentRoute(state, documentId) {
      state.recentRoutes = state.recentRoutes.filter(
        (doc) => !(doc.id === documentId && doc.userId === userId));
      localStorage.setItem("recentRoutes", JSON.stringify(state.recentRoutes)); // Persist to localStorage
    },
  },
  actions: {
    showValidationError: (state, error) => {
      let errors = error.response.data;
      let message = [];
      let i = 0;
      let bootStrapToaster = new BToast();
      for (let [key, val] of Object.entries(errors)) {
        for (let i = 0; i < val.length; i++) {
          message[i] = "";
        }
        for (var index = 0; index < val.length; index++) {
          var error = val[index];
          if (typeof error == "string") {
            message[i++] += `${key}` + ": " + `${error}`;
          }
          else {
            if (!_.isEmpty(error)) {
              if (error.hasOwnProperty("text")) {
                message[i++] = `Index ${index + 1}: ` + error.text;
              }
              else {
                message[i++] = `Index ${index + 1}: Något gick fel!`;
              }
            }
            else {
            }
          }
        };
      }
      for (i = 0; i < message.length; i++) {
        bootStrapToaster.$bvToast.toast(message[i], {
          title: "Error",
          solid: true,
          appendToast: false,
          variant: "danger"
        });
      }
    },
    showOfflineSaved: () => {
      let bootStrapToaster = new BToast();
      bootStrapToaster.$bvToast.toast(i18n.t("offlineSaved"), {
        title: i18n.t("saved"),
        solid: true,
        appendToast: false,
        variant: "warning"
      });
    },
    showOfflineSynced: () => {
      let bootStrapToaster = new BToast();
      bootStrapToaster.$bvToast.toast(i18n.t("offlineSynced"), {
        title: i18n.t("saved"),
        solid: true,
        appendToast: false,
        variant: "success"
      });
    },
    setCompany({ commit }, value) {
      commit("setCompany", value);
    },
    setFocusAreas({ commit }, value) {
      commit("setFocusAreas", value);
    },
    setPendingChanges({ commit }, value) {
      commit("setPendingChanges", value);
    },
    setCreatedProtocolId({ commit }, value) {
      commit("setCreatedProtocolId", value);
    },
    setFrontendLanguage({ commit }, value) {
      i18n.locale = value;

      moment.updateLocale("en", {
        week: {
          dow: 1 // Set week start to Monday for 'en' locale (defaults to Sunday, probably due to US)
        }
      });
      moment.weekdays(true); // Use locale-specific week start
      moment.locale(value);

      // Set language for information we want from backend
      axios.defaults.headers.common["Accept-Language"] = value;
    },
    setPersons({ commit }, value) {
      commit("setPersons", value);
    },
    setDepartments({ commit }, value) {
      commit("setDepartments", value);
    },
    setTags({ commit }, value) {
      commit("setTags", value);
    },
    setEsrsTags({ commit }, value) {
      commit("setEsrsTags", value);
    },
    clearData({ commit }, value) {
      commit("clearData");
    },
    setHelpId({ commit }, value) {
      commit("setHelpId", value);
    },
    setHelpText({ commit }, value) {
      commit("setHelpText", value);
    },
    setHelpSearch({ commit }, value) {
      commit("setHelpSearch", value);
    },
    setHelpsFullList({ commit }, value) {
      commit("setHelpsFullList", value);
    },
    addFavorite({ commit }, document) {
      commit("addFavorite", document);
    },
    removeFavorite({ commit }, documentId) {
      commit("removeFavorite", documentId);
    },
    addRecentRoute({ commit }, document) {
      commit("addRecentRoute", document);
    },
    removeRecentRoute({ commit }, payload) {
      commit("removeRecentRoute", payload);
    },
  },
  getters: {
    company: (state) => {
      return state.company;
    },
    connected: (state) => {
      return state.connected;
    },
    pendingChanges: (state) => {
      return state.pendingChanges;
    },
    offlineQueueCount: (state) => {
      return state.offlineQueueCount;
    },
    createdProtocolId: (state) => {
      return state.createdProtocolId;
    },
    focusAreas: (state) => {
      return state.focusAreas;
    },
    persons: (state) => {
      return state.persons;
    },
    departments: (state) => {
      return state.departments;
    },
    tags: (state) => {
      return state.tags;
    },
    esrstags: (state) => {
      return state.esrstags;
    },
    helpId: (state) => {
      return state.helpId;
    },
    helpText: (state) => {
      return state.helpText;
    },
    helpSearch: (state) => {
      return state.helpSearch;
    },
    helpsFullList: (state) => {
      return state.helpsFullList;
    },
    isFavorite: (state) => (id, userId ) => {
      return state.favorites && state.favorites.some((doc) => doc.id === id && doc.userId === userId );
    },
    getFavorites: (state) => (userId) => {
      return state.favorites ? state.favorites.filter((doc) => doc.userId === userId) : [];
    },
    getRecentRoutes: (state) => (userId) => {
      return state.recentRoutes ? state.recentRoutes.filter((doc) => doc.userId === userId) : [];
    },
  }
};
